/* global data, config */
import { USER_ROLES_NUMERICS_TO_LEVELS } from "constants";
import { useEffect } from "react";
import { Outlet, redirect } from "react-router-dom";

// Material-ui
import { Box, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

// Project imports
import Header from "components/Header";
import Sidebar from "components/Sidebar";
import useLayoutState from "hooks/useLayoutState";
import { drawerWidth } from "themes/constants";
import buildRoute from "components/buildRoute";
import { requireAuthedUser } from "../helpers/auth";
import { identifyUser } from "../configureMixpanel";

// Styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.dark[800]
        : theme.palette.primary.light,
    height: "calc(100vh - 75px)",
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    marginTop: "75px",
    width: `calc(100% - ${drawerWidth}px)`,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: "scroll",
    ...(!open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
      [theme.breakpoints.up("md")]: {
        marginLeft: -drawerWidth,
      },
      [theme.breakpoints.down("md")]: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "72px",
      },
      width: "100%",
    }),
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
      paddingLeft: "30px",
    }),
  }),
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));

  const { isSidebarDrawerOpen, setIsSidebarDrawerOpen } = useLayoutState();

  useEffect(() => {
    setIsSidebarDrawerOpen(!matchDownMd);
  }, [matchDownMd, setIsSidebarDrawerOpen]);

  return (
    <Box sx={{ display: "flex" }}>
      <Header />

      {/* drawer */}
      <Sidebar />

      {/* main content */}
      <Main open={isSidebarDrawerOpen}>
        <Outlet />
      </Main>
    </Box>
  );
};

// RouteObject type per https://github.com/remix-run/react-router/blob/a3e4b8ed875611637357647fcf862c2bc61f4e11/packages/react-router/lib/context.ts#L65
const routeArgs = {
  element: <MainLayout />,
  loader: async (context) => {
    const {
      params: { projectId },
    } = context;

    const user = await requireAuthedUser(context);
    identifyUser(user);

    const allProjectsForUser = user.partners;
    const activeProject = allProjectsForUser.find(
      ({ gcp_project_id }) => gcp_project_id === projectId,
    );

    // Redirect if user doesn't have access to this project
    if (!activeProject) {
      return redirect("/projects");
    }

    const activeProjectRoles = user.roles.filter(
      ({ partner_gcp_project_id }) => partner_gcp_project_id === projectId
    ).map((role) => {
      return { [USER_ROLES_NUMERICS_TO_LEVELS[role.level]]: true };
    });
    const roles = Object.assign({}, ...activeProjectRoles);

    // Fetch project-specific feature flags
    let projectFeatureFlags = {};
    try {
      const response = await fetch(`/api/feature-flags/${projectId}/`);
      if (response.ok) {
        projectFeatureFlags = await response.json();
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }

    const activeProjectDetails = {
      roles: roles,
      project: activeProject,
      featureFlags: projectFeatureFlags,
    };
    const syncPointConfigurations = {};

    return {
      activeProjectDetails,
      allProjectsForUser,
      syncPointConfigurations,
    };
  },
};

export default buildRoute(routeArgs);
